// Ước tính tiền giỏ mua thêm trên cổng khách (kế hoạch D §8.2).
//
// Số THẬT luôn là `payable` máy chủ trả về sau khi đặt, và mã QR phát đúng
// bằng số đó. Nhưng phép tính ở đây phải khớp máy chủ TỪNG ĐỒNG và phải chặn
// đúng những giỏ máy chủ sẽ từ chối — bằng không khách nhìn một số rồi bị thu
// số khác, hoặc bấm đặt xong ăn câu lỗi không hiểu nổi.
//
// Luật gốc: `backend/internal/core/order/upsell_catalog.go` — hàm định giá
// (kind by_size / fixed + perUnit, trần số lượng) và `ShippingFee`.
import type { components } from "@/lib/api/generated/schema";

export type ShopItem = components["schemas"]["UpsellItem"];
export type ShopLine = { item: ShopItem; size: string; qty: number };

/**
 * Trần số lượng của CỔNG KHÁCH (`portalMaxQty` = 200 trong
 * `backend/internal/core/cskh/printorder/customer_portal.go`).
 *
 * Bảng giá dùng chung cho phép tới 500 (`upsellMaxQty`), nhưng cổng khách
 * chặn trước ở 200 — lấy con số rộng hơn là khách đặt được 300 cái trên màn
 * hình rồi bấm đặt mới ăn câu "số lượng không hợp lệ".
 */
export const MAX_QTY = 200;

/** Món tính theo cỡ thì mới có cỡ; chỉ trả cỡ CÓ giá — cỡ giá 0 máy chủ chối. */
export function sizesOf(item: ShopItem): string[] {
  if (item.pricing.kind !== "by_size") return [];
  return Object.entries(item.pricing.sizes ?? {})
    .filter(([, price]) => price > 0)
    .map(([size]) => size);
}

/**
 * Giá cố định mà KHÔNG bật `perUnit` nghĩa là "mua 1 lần" — máy chủ từ chối
 * cả đơn nếu số lượng khác 1, nên màn hình phải khoá nút tăng ở mức 1.
 */
export function maxQtyOf(item: ShopItem): number {
  const p = item.pricing;
  return p.kind === "fixed" && !p.perUnit ? 1 : MAX_QTY;
}

/** Đơn giá; 0 nghĩa là chưa định giá được (chưa chọn cỡ, hoặc cỡ không có giá). */
export function unitPrice(item: ShopItem, size: string): number {
  const p = item.pricing;
  if (p.kind === "by_size") return p.sizes?.[size.trim()] ?? 0;
  return p.price ?? 0;
}

export function lineTotal(item: ShopItem, size: string, qty: number): number {
  if (qty < 1 || qty > MAX_QTY) return 0;
  const unit = unitPrice(item, size);
  if (unit <= 0) return 0;
  // "Mua 1 lần" thì số lượng không nhân vào tiền.
  return maxQtyOf(item) === 1 ? unit : unit * qty;
}

/**
 * `shippingFee` là phí vùng khách đã chọn (chưa chọn vùng thì truyền 0, giống
 * máy chủ coi vùng rỗng là không giao). `freeShipFrom` 0 = không có chương
 * trình miễn ship; đạt ĐÚNG ngưỡng cũng được miễn.
 */
export function cartTotal(
  lines: ShopLine[],
  shippingFee: number,
  freeShipFrom: number,
): { goods: number; shipping: number; total: number } {
  const goods = lines.reduce((sum, l) => sum + lineTotal(l.item, l.size, l.qty), 0);
  const shipping = freeShipFrom > 0 && goods >= freeShipFrom ? 0 : shippingFee;
  return { goods, shipping, total: goods + shipping };
}
